import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spin, Result, Button, message } from "antd";
import { AuthService } from "../services/auth.service";

interface WechatCallbackProps {
  onLoginSuccess: () => void;
}

const WechatCallback: React.FC<WechatCallbackProps> = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleWechatCallback = async () => {
      const code = searchParams.get("code");
      const errorCode = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      // 处理微信授权错误
      if (errorCode) {
        const errorMsg = errorDescription || "微信授权被用户拒绝";
        setError(`微信授权失败: ${errorMsg}`);
        setLoading(false);
        return;
      }

      // 验证必要参数
      if (!code) {
        setError("微信授权失败，未获取到授权码");
        setLoading(false);
        return;
      }

      try {
        // 使用 authService 的 wechatLogin 方法
        const authService = AuthService.getInstance();
        const result = await authService.wechatLogin(code);

        if (result.success && result.data) {
          setSuccess(true);
          // 通知父组件登录成功
          onLoginSuccess();

          navigate("/dashboard", { replace: true });
        } else {
          setError(result.message || "登录失败，请重试");
        }
      } catch (error: any) {
        console.error("微信登录失败:", error);

        let errorMessage = "登录失败，请重试";

        // 处理不同类型的错误
        if (error?.response?.status === 401) {
          errorMessage = "该微信账号不是平台管理员";
        } else if (error?.response?.status === 403) {
          errorMessage = "您没有访问权限";
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    handleWechatCallback();
  }, [searchParams, onLoginSuccess]);

  const handleRetryLogin = () => {
    // 清除可能的错误状态
    setError("");
    navigate("/login", { replace: true });
  };

  // 通用的容器样式
  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  };

  const cardStyle = {
    background: "white",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    maxWidth: "400px",
    width: "90%",
  };

  // 加载状态
  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: "center" as const }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px", fontSize: "16px", color: "#666" }}>
            正在验证登录信息...
          </div>
          <div style={{ marginTop: "8px", fontSize: "14px", color: "#999" }}>
            请稍候，这可能需要几秒钟
          </div>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <Result
            status="error"
            title="登录失败"
            subTitle={error}
            extra={[
              <Button type="primary" key="retry" onClick={handleRetryLogin}>
                重新登录
              </Button>,
            ]}
          />
        </div>
      </div>
    );
  }

  // 成功状态
  if (success) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <Result
            status="success"
            title="登录成功"
            subTitle="正在跳转到管理后台..."
            extra={
              <div
                style={{ textAlign: "center", color: "#666", fontSize: "14px" }}
              >
                如果页面没有自动跳转，请{" "}
                <Button type="link" onClick={() => navigate("/dashboard")}>
                  点击这里
                </Button>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  // 默认加载状态（理论上不会到达这里）
  return (
    <div style={containerStyle}>
      <div style={{ ...cardStyle, textAlign: "center" as const }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px", fontSize: "16px", color: "#666" }}>
          处理中...
        </div>
      </div>
    </div>
  );
};

export default WechatCallback;
